import { useState, useCallback } from "react";
import axiosVortex from "../../../utils/axios.instance";
import type { OrderDataFormerProbs } from "../../../models/orders";
import BrokerApi from "../../../chart-brokerapi/broker";
import { DataFeedInstance } from "../../../chart-datafeed/udf-compatible-datafeed";

interface UseOrderRequestResponse {
  data: any;
  loading: boolean;
  error: string | null;
  mutate: (payload: OrderDataFormerProbs, options?: { 
    onSuccess?: (data: any) => void; 
    onError?: (error: string) => void;
    origin?: string; // Add origin parameter
  }) => Promise<void>;
}

const useOrderRequest = (): UseOrderRequestResponse => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const placeOrder = useCallback(async (payload: any) => {
    const partialFinalURL = payload?.legs ? 'trading/orders/iceberg': 'trading/orders/regular';
    const response = await axiosVortex.post(partialFinalURL, payload);
    
    if (response.data.status === "success") {
      return response.data;
    } else {
      throw new Error(response.data.message || "Order request failed");
    }
  }, []);

  const mutate = useCallback(
    async (payload: any, options?: { 
      onSuccess?: (data: any) => void; 
      onError?: (error: string) => void;
      origin?: string;
    }) => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        // Check for surveillance measures before placing the order
        if (payload.symbol) {
          await new Promise<void>((resolve, reject) => {
            DataFeedInstance?.resolveSymbolInfo(String(payload.symbol), 
              (symbolInfo) => {
                if (symbolInfo.asmGsmStage) {
                  if (options?.origin === "buy_sell_button") {
                    // Hide option chain if needed
                    const element = document.getElementById("optionChainNew");
                    if (element) {
                      element.style.display = "none";
                    }
                    
                    // Show confirmation dialog
                    window.tvWidget.showConfirmDialog({
                      title: "Disclaimer",
                      body: `Security is under Surveillance Measure- ${symbolInfo.asmGsmStage}. Do you wish you continue?`,
                      callback: async (confirmed) => {
                        if (confirmed) {
                          try {
                            const result = await placeOrder(payload);
                            setData(result);
                            options?.onSuccess?.(result);
                          } catch (err: any) {
                            handleError(err, options);
                          }
                        }
                        
                        // Show option chain again
                        const element = document.getElementById("optionChainNew");
                        if (element) {
                          element.style.display = "block";
                        }
                        
                        setLoading(false);
                      }
                    });
                    resolve();
                    return;
                  } else {
                    // If not from buy_sell_button but still under surveillance
                    window.tvWidget.showConfirmDialog({
                      title: "Disclaimer",
                      body: `Security is under Surveillance Measure- ${symbolInfo.asmGsmStage}. Do you wish you continue?`,
                      callback: async (confirmed) => {
                        if (confirmed) {
                          try {
                            const result = await placeOrder(payload);
                            setData(result);
                            options?.onSuccess?.(result);
                          } catch (err: any) {
                            handleError(err, options);
                          }
                        }
                        setLoading(false);
                      }
                    });
                    resolve();
                    return;
                  }
                } else {
                  // No surveillance measure, proceed with order
                  resolve();
                }
              },
              () => {
                // Error resolving symbol
                reject(new Error("Failed to resolve symbol"));
              },
              ''
            );
          });
          
          // If we got here and loading is still true, it means we need to place the order
          // (No surveillance measure case)
          if (loading) {
            const result = await placeOrder(payload);
            setData(result);
            options?.onSuccess?.(result);
          }
        } else {
          // No symbol provided, just place the order
          const result = await placeOrder(payload);
          setData(result);
          options?.onSuccess?.(result);
        }
      } catch (err: any) {
        handleError(err, options);
        setLoading(false);
      }
    },
    [placeOrder]
  );

  const handleError = (err: any, options?: { onError?: (error: string) => void }) => {
    const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";
    setError(errorMessage);
    options?.onError?.(errorMessage);
    
    // Handle 500 status code specifically
    if (err.response?.data.status === 'error') {
      BrokerApi.instance?._host.showNotification(
        "Order placement",
        err.response?.data.message,
        0
      );
    }
  };

  return { data, loading, error, mutate };
};

export default useOrderRequest;